import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ---------------------------------------------------------------------------
// Inject ?country=XX on every request from localStorage (set by CountryProvider).
// Pages that explicitly send `country` in their own params override this.
// Authentication, countries listing, and refresh endpoints are exempt — they
// are global by design.
// ---------------------------------------------------------------------------
const COUNTRY_EXEMPT = ["/auth/", "/countries"];

api.interceptors.request.use((config) => {
  const url = config.url || "";
  const exempt = COUNTRY_EXEMPT.some((p) => url.startsWith(p));
  if (!exempt) {
    const code = localStorage.getItem("rail_country") || "TR";
    config.params = { country: code, ...(config.params || {}) };
  }
  return config;
});

// ---------------------------------------------------------------------------
// Automatic refresh-on-401 interceptor.
// On a 401 (except from /auth/* itself), call POST /auth/refresh exactly once
// and replay the original request. Concurrent 401s wait for the same refresh.
// ---------------------------------------------------------------------------
let refreshing = null;

api.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || "";

    const isAuthCall = url.includes("/auth/login") ||
                       url.includes("/auth/refresh") ||
                       url.includes("/auth/logout") ||
                       url.includes("/auth/register");

    if (status === 401 && !original._retry && !isAuthCall) {
      original._retry = true;
      try {
        if (!refreshing) {
          refreshing = api.post("/auth/refresh").finally(() => {
            // small delay so concurrent callers reuse the same in-flight refresh
            setTimeout(() => { refreshing = null; }, 0);
          });
        }
        await refreshing;
        return api(original);
      } catch (e) {
        // refresh failed → propagate the original 401 so the app redirects to /login
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const fmtMoney = (v, currency = "EUR") => {
  if (v == null || isNaN(v)) return "—";
  const n = Number(v);
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(2)} M€`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)} k€`;
  return `${n.toFixed(0)} €`;
};

export const fmtInt = (v) => {
  if (v == null || isNaN(v)) return "—";
  return Number(v).toLocaleString("en-US");
};

export const fmtPct = (v) => {
  if (v == null || isNaN(v)) return "—";
  return `${Number(v).toFixed(1)} %`;
};

export default api;
